import { type ButtonHTMLAttributes, createElement } from 'react'
import { isNonNil } from 'utils'

import type { SpriteMap, SpriteMap16, SpriteMapCustom, SpriteMapPredefined } from '../global'
import { createVariants } from '../lib'
import { type DisabledGuard, DisabledReason, findActiveGuard, swallowClick } from './DisabledGuard'
import { Icon, type IconRotation } from './Icon'

export type TextButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-disabled'> & {
  icon?: SpriteMap | SpriteMap16 | SpriteMapCustom | SpriteMapPredefined
  iconLeft?: true
  iconRotation?: IconRotation
  iconSize?: `size-${string}`
  /**
   * The button is disabled while any guard holds, and the first holding guard's reason is shown
   * when the button is hovered or focused. This replaces a bare `disabled`, so that a disabled
   * button always says why.
   */
  disabledWhen?: DisabledGuard | ReadonlyArray<DisabledGuard>
  /** Not accepted, including through a spread: use `disabledWhen`, which carries the reason. */
  disabled?: never
}

// Inactive buttons carry `aria-disabled` rather than the native `disabled` attribute. A natively
// disabled element receives no pointer or focus events, so nothing could open the reason tooltip.
const textButtonClasses = createVariants({
  base: 'flex h-fit items-center gap-2 place-content-center pan-body whitespace-nowrap bg-transparent p-0 text-green-500 hover:text-green-700 active:text-green-800 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:text-green-500'
})

const iconClasses = createVariants({ base: 'size-4 shrink-0' })

// TODO: Discuss if our button components should default to type='submit' (HTML) or type='button'
// createElement bypasses the merged JSX.IntrinsicElements global that arises from
// duplicate @types/react instances in the mobile compilation (hoistingLimits: workspaces).
const TextButtonElement = (props: ButtonHTMLAttributes<HTMLButtonElement>) =>
  createElement('button', { type: 'submit', ...props })

export const TextButton = ({
  children,
  icon,
  iconLeft,
  iconRotation,
  iconSize,
  className,
  disabledWhen,
  onClick,
  ...rest
}: TextButtonProps) => {
  const activeGuard = findActiveGuard(disabledWhen)

  return (
    <DisabledReason reason={activeGuard?.reason}>
      <TextButtonElement
        {...rest}
        aria-disabled={activeGuard !== undefined ? true : undefined}
        onClick={activeGuard !== undefined ? swallowClick : onClick}
        className={textButtonClasses({ className })}
      >
        {iconLeft && isNonNil(icon) && (
          <Icon
            icon={icon}
            rotation={iconRotation}
            className={iconClasses({ className: iconSize })}
          />
        )}
        {children}
        {!iconLeft && isNonNil(icon) && (
          <Icon
            icon={icon}
            rotation={iconRotation}
            className={iconClasses({ className: iconSize })}
          />
        )}
      </TextButtonElement>
    </DisabledReason>
  )
}

TextButton.displayName = 'TextButton'
